// src/components/forms/ChangePasswordForm.tsx
import { Form, Input, Button, Card, Tooltip } from 'antd';
import { useState } from 'react'; // 补充缺失的导入
import { LockOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { updatePassword } from '@/api/developer';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import type { UpdatePasswordRequest } from '@/types/auth';

/**
 * 修改密码表单组件
 */
export const ChangePasswordForm = () => {
  const [form] = Form.useForm<UpdatePasswordRequest>();
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * 处理表单提交
   */
  const onFinish = async (values: UpdatePasswordRequest): Promise<void> => {
    setSubmitting(true);
    setError(null);

    try {
      await updatePassword(values);
      form.resetFields();
      window.location.href = '/login';
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '修改密码失败';
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      {/* 标题区域 - 与AppForm风格统一 */}
      <div className="mb-10 text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-linear-to-r from-blue-500 to-purple-600 rounded-full mb-6">
          <LockOutlined className="text-white text-2xl" />
        </div>
        <h2 className="text-2xl font-bold text-gray-800 mb-3">修改登录密码</h2>
        <p className="text-gray-600 max-w-2xl mx-auto">
          请输入当前密码并设置新密码，新密码请妥善保管。
        </p>
      </div>
      
      {/* 错误提示 - 与AppForm间距、样式统一 */}
      {error && (
        <div className="mb-8">
          <ErrorAlert 
            message="操作失败" 
            description={error} 
          />
        </div>
      )}

      {/* 表单卡片 - 与AppForm完全一致的卡片样式 */}
      <Card 
        className="shadow-lg border-0 rounded-xl"
        styles={{ body: { padding: '32px' } }}
      >
        <Form
          form={form}
          name="change_password"
          layout="vertical"
          onFinish={onFinish}
          requiredMark={false}
          className="space-y-8" // 与AppForm统一的表单间距
        >
          {/* 密码设置区域 - 仿AppForm的分区样式 */}
          <div className="space-y-6">
            {/* <div className="flex items-center space-x-3 mb-5">
              <div className="w-2 h-7 bg-blue-500 rounded"></div>
              <h3 className="text-lg font-semibold text-gray-800">密码设置</h3>
            </div> */}

            {/* 原密码输入框 */}
            <Form.Item
              name="old_password"
              label={
                <span className="font-medium text-gray-700 flex items-center">
                  当前密码
                  <span className="text-red-500 ml-1">*</span>
                  <Tooltip title="请输入您当前的登录密码">
                    <QuestionCircleOutlined className="ml-2 text-gray-400 text-sm" />
                  </Tooltip>
                </span>
              }
              rules={[{ required: true, message: '请输入当前密码' }]}
              className="mb-0"
            >
              <Input.Password
                placeholder="请输入当前登录密码"
                size="large"
                disabled={submitting}
                className="rounded-lg border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-colors"
              />
            </Form.Item>

            {/* 新密码输入框 */}
            <Form.Item
              name="new_password"
              label={
                <span className="font-medium text-gray-700 flex items-center">
                  新密码
                  <span className="text-red-500 ml-1">*</span>
                  <Tooltip title="新密码至少6位，建议包含字母和数字组合。">
                    <QuestionCircleOutlined className="ml-2 text-gray-400 text-sm" />
                  </Tooltip>
                </span>
              }
              rules={[
                { required: true, message: '请输入新密码' },
                { min: 6, message: '新密码至少6位' },
              ]}
              className="mb-0"
            >
              <Input.Password
                placeholder="请设置新的登录密码"
                size="large"
                disabled={submitting}
                className="rounded-lg border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-colors"
              />
            </Form.Item>

            {/* 确认新密码 */}
            <Form.Item
              name="confirm_password"
              label={
                <span className="font-medium text-gray-700 flex items-center">
                  确认新密码
                  <span className="text-red-500 ml-1">*</span>
                  <Tooltip title="请再次输入新密码以确认">
                    <QuestionCircleOutlined className="ml-2 text-gray-400 text-sm" />
                  </Tooltip>
                </span>
              }
              dependencies={['new_password']}
              rules={[
                { required: true, message: '请确认新密码' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('new_password') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('两次输入的密码不一致'));
                  },
                }),
              ]}
              className="mb-0"
            >
              <Input.Password
                placeholder="请再次输入新密码"
                size="large"
                disabled={submitting}
                className="rounded-lg border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-colors"
              />
            </Form.Item>
          </div>

          {/* 提交按钮 - 与AppForm统一的按钮样式、布局 */}
          <Form.Item className="mb-0 pt-6">
            <div className="flex justify-end space-x-5">
              <Button
                size="large"
                onClick={() => form.resetFields()}
                disabled={submitting}
                className="px-7 py-2.5 border-gray-300 hover:border-gray-400 hover:bg-gray-50 transition-colors rounded-lg"
              >
                重置
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                loading={submitting}
                disabled={submitting}
                className="px-9 py-2.5 bg-linear-to-r from-blue-500 to-purple-600 border-0 hover:from-blue-600 hover:to-purple-700 text-white font-medium shadow-md hover:shadow-lg transition-all duration-200 rounded-lg"
              >
                {submitting ? '修改中...' : '确认修改'}
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};